/**
 * The menu look (docs/spec/04 §7): the colours, spacing and clocks every screen shares, so they all
 * read as one game. Colours are C64 palette indices only.
 */

import {
  BLACK, BLUE, BROWN, CYAN, DARK_GREY, GREEN, GREY, LIGHT_BLUE, LIGHT_GREEN, LIGHT_GREY, LIGHT_RED, ORANGE, PURPLE, RED,
  WHITE, YELLOW,
} from '../../render/palette';

/** The menu virtual screen. */
export const W = 320;
export const H = 200;

/** Plain text. */
export const INK = WHITE;
/** The selected item. */
export const SELECTED = YELLOW;
/** Secondary text: hints, labels. */
export const MUTED = GREY;
/** Tertiary: frames, disabled items. */
export const FAINT = DARK_GREY;
/** Group headings, in the cave-A colours (04 §7). */
export const HEADING = ORANGE;
/** Values and highlights. */
export const ACCENT = CYAN;
export const GOOD = LIGHT_GREEN;
export const BAD = LIGHT_RED;
export const BG = BLACK;

/** Cave A's colours: how-to-play sprites and the logo stone. */
export const CAVE_A: readonly [number, number, number] = [ORANGE, GREY, WHITE];

/** Colour ramps for raster bars, dark to bright to dark, as a C64 demo would cycle them. */
export const RAMPS = {
  blue: [BLUE, LIGHT_BLUE, CYAN, WHITE, CYAN, LIGHT_BLUE, BLUE],
  fire: [BROWN, RED, ORANGE, LIGHT_RED, YELLOW, WHITE, YELLOW, LIGHT_RED, ORANGE, RED, BROWN],
  green: [DARK_GREY, GREEN, LIGHT_GREEN, WHITE, LIGHT_GREEN, GREEN, DARK_GREY],
  grey: [DARK_GREY, GREY, LIGHT_GREY, WHITE, LIGHT_GREY, GREY, DARK_GREY],
  purple: [BLUE, PURPLE, LIGHT_RED, LIGHT_GREY, WHITE, LIGHT_GREY, LIGHT_RED, PURPLE, BLUE],
} as const satisfies Record<string, readonly number[]>;

/** A bright cycle for sine-scroller letters and "new" highlights. */
export const RAINBOW: readonly number[] = [YELLOW, WHITE, CYAN, LIGHT_BLUE, PURPLE, LIGHT_RED, ORANGE, LIGHT_GREEN];

/** Standard menu row height: 8 px glyphs with room to breathe and to hit with a finger. */
export const ROW = 11;

/** The 25 Hz animation clock the sprites use (04 §3). */
export function animFrame(ms: number): number {
  return Math.floor(ms / 40);
}

/** On for half of every `period` ms. */
export function blink(ms: number, period = 500): boolean {
  return Math.floor(ms / (period / 2)) % 2 === 0;
}

let calm = false;

/** Reduce flashing (07 §5): menus keep their colour cycles, but slowed right down. */
export function setCalm(on: boolean): void {
  calm = on;
}

/** How long a colour-cycle step lasts, given the wanted `stepMs` and the Reduce flashing setting. */
export function calmStep(stepMs: number): number {
  return calm ? Math.max(stepMs, 450) : stepMs;
}

/** A colour from a cycle, stepping every `stepMs`. */
export function cycle(colours: readonly number[], ms: number, stepMs = 80, offset = 0): number {
  const n = colours.length;
  const i = (((Math.floor(ms / calmStep(stepMs)) + offset) % n) + n) % n;
  return colours[i] ?? WHITE;
}

/** Smooth 0..1 for a transition that started `ms` ago and lasts `total` ms. */
export function ease(ms: number, total: number): number {
  const p = Math.min(1, Math.max(0, ms / total));
  return 1 - (1 - p) * (1 - p) * (1 - p);
}
